import { compareDesc } from 'date-fns';

import {
    CommitmentDto,
    EventResult,
    FocusContainerSummaryDto,
    InteractionSummaryDto,
} from '../../coaching-base/general';
import { ObjectDateTransformer } from '../../coaching-insight/coaching-insight.model';
import { ObservationSummaryDtoSetTransformer } from '../../coaching-observation/observation/observation-summary-dto-set.transformer';
import { ModelTransformer } from '../../common/common.model';
import { pluck } from '../../store.decorator';

// this can be done on the api side. with new unsaved linked observations on the ui side being put at the top of the list
export function sortObservationsByLinkTime<
    T extends {
        event: EventResult;
        focusContainers: FocusContainerSummaryDto[];
    },
>(items: T[]): T[] {
    let sortedInteractionLinkTime: number[] = [];

    const findIndex = (item: T): number => {
        return sortedInteractionLinkTime.findIndex((e) => {
            if (!item.focusContainers) {
                return false;
            }
            return item.focusContainers.some((fc) => {
                if (!fc.interactions) {
                    return false;
                }
                return fc.interactions.some((ee) => ee.interactionId === e);
            });
        });
    };

    const sortFn = (a: T, b: T): number => {
        let ret = findIndex(a) - findIndex(b);
        if (ret === 0) {
            ret = compareDesc(a.event.sessionDate, b.event.sessionDate);
        }
        return ret;
    };

    if (!items) {
        return [];
    }

    sortedInteractionLinkTime = pluck<InteractionSummaryDto[]>(
        items,
        'focusContainers[].interactions[]',
    )
        .filter((e) => e.linkedOn != null)
        .sort((a, b) => {
            return compareDesc(a.linkedOn, b.linkedOn);
        })
        .map((e) => e.interactionId);

    return (items || []).sort(sortFn);
}

export class CommitmentDtoTransformer
    implements ModelTransformer<CommitmentDto, CommitmentDto>
{
    transform(model: CommitmentDto): CommitmentDto {
        const dateTransformer = new ObjectDateTransformer<CommitmentDto>({
            startDate: 'L',
            endDate: 'L',
            events: {
                sessionDate: 'L',
                createdOn: 'L',
                updatedOn: 'L',
                startTime: 'L',
                endTime: 'L',
            },
            links: {
                createdOn: 'L',
                updatedOn: 'L',
            },
            goals: {
                dateSet: 'L',
                createdOn: null,
                updatedOn: null,
                estimatedCompletionDate: 'L',
            },
            createdOn: 'L',
            pendingActiveDate: null,
            activeDate: null,
            pendingCloseDate: null,
            closedDate: null,
            timelineLatestUpdate: {
                timeline: {
                    date: null,
                },
                latestUpdate: {
                    date: null,
                },
            },
        });
        const next = dateTransformer.transform(model);

        if (next.commitmentId === 0) {
            next.commitmentId = null;
        }

        const observationSummaryTransformer =
            new ObservationSummaryDtoSetTransformer();
        const observations = observationSummaryTransformer.transform(
            next.observations,
        );

        next.observations = sortObservationsByLinkTime(observations);

        next.goals = (next.goals || []).sort(
            (a, b) => +a.createdOn - +b.createdOn,
        );

        return next;
    }
}
